import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  getContentSectionsList,
  deleteContentSection,
} from "../../../services/contentSectionsService";
import { paginateData, calculateTotalPages } from "../../../assets/js/script";
import TableHeader from "../../Common/TableComponent/TableHeader";
import TableToolbar from "../../Common/TableComponent/TableToolbar";
import TablesRow from "../../Common/TableComponent/TablesRow";
import { renderStatusBadge } from "../../Common/TableComponent/tableBadges";
import { Pagination } from "../../Common/TableComponent/Pagination";
import { Loading } from "../../Common/OtherElements/Loading";
import Swal from "sweetalert2";
import { confirmDelete } from "../../Common/OtherElements/confirmDeleteClone";
import { TableDataStatusError } from "../../Common/OtherElements/TableDataStatusError";
import { handleErrors } from "../../../utils/errorHandler";
import { usePageLevelAccess } from "../../../hooks/usePageLevelAccess";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";

const getSectionId = (item) => item.id ?? item.Id;

const getSectionName = (item) =>
  item.sectionName || item.SectionName || "-";

const getSectionSlug = (item) =>
  item.sectionSlug || item.SectionSlug || "-";

const getSectionLayout = (item) =>
  item.sectionLayout || item.SectionLayout || "-";

const getDisplayOrder = (item) =>
  item.displayOrder ?? item.DisplayOrder ?? "-";

const isTruthyFlag = (item, camelKey, pascalKey) => {
  const value = item[camelKey] ?? item[pascalKey];
  return value === true || value === "true" || value === 1;
};

const renderFlagBadge = (isOn, onLabel, offLabel) => (
  <span className={`badge badge-soft-${isOn ? "success" : "secondary"}`}>
    {isOn ? onLabel : offLabel}
  </span>
);

export const ManageContentSections = () => {
  const [pageAccessDetails, setPageAccessDetails] = useState({});
  const pageLevelAccessUrl = "content-sections";
  const navigate = useNavigate();
  const { pageAccessData } = usePageLevelAccess(pageLevelAccessUrl);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [sectionsList, setSectionsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (pageAccessData) {
      if (!pageAccessData.viewAccess) {
        navigate("/404-error-page");
      } else {
        setPageAccessDetails(pageAccessData);
      }
    }
  }, [pageAccessData, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getContentSectionsList();
      const list = response.data?.result || response.data || [];
      setSectionsList(Array.isArray(list) ? list : []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredData = sectionsList.filter((item) => {
    const name = getSectionName(item).toLowerCase();
    const slug = getSectionSlug(item).toLowerCase();
    const layout = getSectionLayout(item).toLowerCase();
    const query = searchQuery.toLowerCase();
    return (
      name.includes(query) ||
      slug.includes(query) ||
      layout.includes(query)
    );
  });

  const currentData = paginateData(filteredData, currentPage, entriesPerPage);
  const totalPages = calculateTotalPages(filteredData.length, entriesPerPage);

  const handleEntriesChange = (value) => {
    setEntriesPerPage(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDelete = async (sectionId) => {
    const confirmed = await confirmDelete("Content Section");
    if (confirmed) {
      try {
        await deleteContentSection(sectionId);
        setSectionsList((prev) =>
          prev.filter((item) => getSectionId(item) !== sectionId)
        );
        Swal.fire(
          "Deleted!",
          "The content section has been deleted successfully.",
          "success"
        );
        fetchData();
      } catch (error) {
        handleErrors(error);
      }
    }
  };

  return (
    <>
      <ComponentHeader title="Content Sections" />

      {pageAccessDetails.viewAccess ? (
        <div className="row">
          <div className="col-xxl-12">
            <div className="card mt-xxl-n5">
              <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
                <h5 className="mb-0">Manage Content Sections</h5>
                {pageAccessDetails.addAccess ? (
                  <Link
                    to="/content-sections/add"
                    className="btn btn-secondary btn-sm"
                  >
                    <i className="ri-add-line align-middle me-1"></i>
                    Add Section
                  </Link>
                ) : null}
              </div>
              <div className="card-body manage-amenity-master-card-body">
                <TableToolbar
                  entriesPerPage={entriesPerPage}
                  onEntriesChange={handleEntriesChange}
                  searchValue={searchQuery}
                  searchPlaceholder="Search..."
                  onSearchChange={(e) => {
                    setSearchQuery(e.target.value);
                    setCurrentPage(1);
                  }}
                />

                {loading ? (
                  <Loading />
                ) : (
                  <div className="table-responsive">
                    <table className="table align-middle table-bordered">
                      <TableHeader
                        columns={[
                          "Sl No",
                          "Section Name",
                          "Section Slug",
                          "Section Layout",
                          "Featured",
                          "Featured Home",
                          "Display Order",
                          "Status",
                          "Action",
                        ]}
                      />
                      <tbody className="manage-page-group-table-values">
                        {currentData.length === 0 ? (
                          <TableDataStatusError colspan="9" />
                        ) : (
                          currentData.map((item, index) => {
                            const isFeatured = isTruthyFlag(
                              item,
                              "isFeatured",
                              "IsFeatured"
                            );
                            const isFeaturedHome = isTruthyFlag(
                              item,
                              "isFeaturedHome",
                              "IsFeaturedHome"
                            );

                            return (
                              <TablesRow
                                key={getSectionId(item)}
                                rowData={{
                                  slNo:
                                    (currentPage - 1) * entriesPerPage +
                                    index +
                                    1,
                                  sectionName: getSectionName(item),
                                  sectionSlug: getSectionSlug(item),
                                  sectionLayout: getSectionLayout(item),
                                  featured: renderFlagBadge(
                                    isFeatured,
                                    "Yes",
                                    "No"
                                  ),
                                  featuredHome: renderFlagBadge(
                                    isFeaturedHome,
                                    "Yes",
                                    "No"
                                  ),
                                  displayOrder: getDisplayOrder(item),
                                  status: renderStatusBadge(item),
                                }}
                                columns={[
                                  "slNo",
                                  "sectionName",
                                  "sectionSlug",
                                  "sectionLayout",
                                  "featured",
                                  "featuredHome",
                                  "displayOrder",
                                  "status",
                                ]}
                                hideIcons={false}
                                onEdit={() => {
                                  navigate(
                                    `/content-sections/update/${getSectionId(item)}`
                                  );
                                }}
                                onDelete={() =>
                                  handleDelete(getSectionId(item))
                                }
                                pageLevelAccessData={pageAccessDetails}
                              />
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalEntries={filteredData.length}
                  entriesPerPage={entriesPerPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        ""
      )}
    </>
  );
};
